import { useEffect, useRef, useState } from 'react'
import {
  Modal,
  Button,
  Group,
  Text,
  Stack,
  Box,
  Loader,
  Switch,
  Alert
} from '@mantine/core'
import { FiAlertTriangle, FiShield } from 'react-icons/fi'
import { toast } from 'react-toastify'
import {
  detectSerialRegions,
  applyBlurs,
  type DetectedRegion,
  type OcrStatus
} from './blurSerialNumbers'

const ShieldIcon = FiShield as React.FC<{ size?: number }>
const AlertIcon = FiAlertTriangle as React.FC<{ size?: number }>

interface Props {
  file: File | null;
  onConfirm: (result: File) => void;
  onSkip: (original: File) => void;
  onClose: () => void;
}

const STATUS_LABELS: Record<OcrStatus, string> = {
  idle: '',
  loading: 'Loading OCR engine…',
  recognising: 'Scanning for serial numbers…',
  done: '',
  error: 'Detection failed'
}

interface DebugInfo {
  file: { name: string; size: string; type: string };
  compressedSize?: string;
  canvasSupport: boolean;
  error?: string;
}

/** Downscale + re-encode as JPEG so large camera shots don't hit the 413 limit. */
function compressImage (file: File, maxDim = 2048, quality = 0.85): Promise<File> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      const { naturalWidth: w, naturalHeight: h } = img
      const scale = Math.min(1, maxDim / Math.max(w, h))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(w * scale)
      canvas.height = Math.round(h * scale)
      const ctx = canvas.getContext('2d')
      if (!ctx) { reject(new Error('Canvas 2D not supported')); return }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Compression canvas.toBlob returned null')); return }
          const base = file.name.replace(/\.[^.]+$/, '') || 'photo'
          resolve(new File([blob], `${base}.jpg`, { type: 'image/jpeg' }))
        },
        'image/jpeg',
        quality
      )
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image for compression')) }
    img.src = url
  })
}

/** Fills modal content; parent must be a flex column with bounded height (fullScreen modal). */
function ModalBodyShell (props: { children?: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
        maxHeight: '100%',
        overflow: 'hidden'
      }}
    >
      {props.children}
    </div>
  )
}

export default function SerialBlurModal ({ file, onConfirm, onSkip, onClose }: Props) {
  const [regions, setRegions] = useState<DetectedRegion[]>([])
  const [ocrStatus, setOcrStatus] = useState<OcrStatus>('idle')
  const [applying, setApplying] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [applyError, setApplyError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  // Set to true when the user chooses "Upload without blurring" mid-apply so the
  // in-flight applyBlurs result is discarded instead of uploading a second time.
  const skipRef = useRef(false)

  const opened = file !== null

  // Build preview URL whenever file changes
  useEffect(() => {
    if (!file) { setPreviewUrl(null); return }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  // Draw overlays on canvas whenever regions change
  useEffect(() => {
    if (!previewUrl || !canvasRef.current) return

    const img = new Image()
    img.onload = () => {
      imgRef.current = img
      redrawCanvas()
    }
    img.src = previewUrl
  }, [previewUrl])

  useEffect(() => {
    if (imgRef.current) redrawCanvas()
  }, [regions])

  function redrawCanvas () {
    const canvas = canvasRef.current
    const img = imgRef.current
    const container = containerRef.current
    if (!canvas || !img || !container) return

    // Compute where the image actually renders inside the container (objectFit: contain).
    // Without this the canvas pixel dimensions (naturalW × naturalH) get CSS-stretched to
    // the container dimensions, which have a different aspect ratio → squished overlay.
    const cw = container.clientWidth
    const ch = container.clientHeight
    const scale = Math.min(cw / img.naturalWidth, ch / img.naturalHeight)
    const rw = Math.round(img.naturalWidth * scale)
    const rh = Math.round(img.naturalHeight * scale)
    canvas.style.left = `${Math.round((cw - rw) / 2)}px`
    canvas.style.top = `${Math.round((ch - rh) / 2)}px`
    canvas.style.width = `${rw}px`
    canvas.style.height = `${rh}px`

    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(img, 0, 0)

    for (const r of regions) {
      const rx = r.x * img.naturalWidth
      const ry = r.y * img.naturalHeight
      const rw = r.w * img.naturalWidth
      const rh = r.h * img.naturalHeight

      ctx.save()
      ctx.strokeStyle = r.blur ? '#f59e0b' : 'rgba(255,255,255,0.4)'
      ctx.lineWidth = Math.max(2, img.naturalWidth * 0.003)
      ctx.strokeRect(rx, ry, rw, rh)
      if (r.blur) {
        ctx.fillStyle = 'rgba(245,158,11,0.18)'
        ctx.fillRect(rx, ry, rw, rh)
      }
      ctx.restore()
    }
  }

  async function runDetection () {
    if (!file) return
    setRegions([])
    const baseDebug: DebugInfo = {
      file: { name: file.name, size: `${(file.size / 1024 / 1024).toFixed(2)} MB`, type: file.type || '(empty)' },
      canvasSupport: !!document.createElement('canvas').getContext('2d')
    }
    setDebugInfo(baseDebug)
    try {
      const found = await detectSerialRegions(file, setOcrStatus)
      setRegions(found)
    } catch (err: any) {
      setOcrStatus('error')
      const errStr = [err?.name, err?.message, err?.stack].filter(Boolean).join('\n')
      setDebugInfo({ ...baseDebug, error: errStr })
    }
  }

  async function handleConfirm () {
    if (!file || uploading) return
    skipRef.current = false
    setApplying(true)
    setApplyError(null)
    try {
      const compressed = await compressImage(file)
      setDebugInfo(prev => prev ? { ...prev, compressedSize: `${(compressed.size / 1024 / 1024).toFixed(2)} MB` } : prev)
      const result = await applyBlurs(compressed, regions)
      if (skipRef.current) return
      setApplying(false)
      setUploading(true)
      await Promise.resolve(onConfirm(result))
    } catch (err: any) {
      const errStr = [err?.name, err?.message, err?.stack].filter(Boolean).join('\n')
      setApplyError(errStr)
      setDebugInfo(prev => ({
        file: prev?.file ?? { name: file.name, size: `${(file.size / 1024 / 1024).toFixed(2)} MB`, type: file.type || '(empty)' },
        compressedSize: prev?.compressedSize,
        canvasSupport: prev?.canvasSupport ?? !!document.createElement('canvas').getContext('2d'),
        error: `[applyBlurs] ${errStr}`
      }))
      toast.error(err instanceof Error ? err.message : 'Could not blur or upload the image. Try "Upload without blurring".')
    } finally {
      setApplying(false)
      setUploading(false)
    }
  }

  async function handleSkipUpload () {
    if (!file || uploading || applying) return
    skipRef.current = true
    setUploading(true)
    try {
      const compressed = await compressImage(file)
      await Promise.resolve(onSkip(compressed))
    } catch (e) {
      console.error(e)
      toast.error(e instanceof Error ? e.message : 'Upload failed.')
    } finally {
      setUploading(false)
    }
  }

  function toggleRegion (id: string) {
    setRegions((prev) =>
      prev.map((r) => (r.id === id ? { ...r, blur: !r.blur } : r))
    )
  }

  const toBlurCount = regions.filter((r) => r.blur).length
  const isScanning = ocrStatus === 'loading' || ocrStatus === 'recognising'

  const actionBtnSx = {
    minHeight: 58,
    touchAction: 'manipulation' as const,
    width: '100%',
    fontSize: 17,
    paddingTop: 14,
    paddingBottom: 14
  }

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (uploading || applying) return
        onClose()
      }}
      trapFocus={false}
      /* Outside taps on mobile Safari often race the upload buttons; keep dismiss explicit (X / Escape when idle). */
      closeOnClickOutside={false}
      closeOnEscape={!uploading && !applying}
      scrollAreaComponent={ModalBodyShell}
      zIndex={450}
      /* Edge-to-edge: prior size/xl + width overrides were still a small centered card on many layouts */
      fullScreen
      title={
        <Group spacing="md">
          <ShieldIcon size={26} />
          <Text weight={700} size="xl">
            Serial number protection
          </Text>
        </Group>
      }
      padding={0}
      styles={
        {
          inner: { padding: 0 },
          content: {
            borderRadius: 0,
            maxHeight: '100dvh',
            height: '100dvh',
            maxWidth: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          },
          header: {
            padding: '16px 20px',
            marginBottom: 0,
            flexShrink: 0,
            borderBottom: '1px solid var(--gs-border)'
          },
          body: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            padding: '20px 24px 16px'
          },
          close: { width: 44, height: 44 }
        } as any
      }
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        {(uploading || applying) && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.35)',
              borderRadius: 8
            }}
          >
            <Text size="lg" color="white" weight={700}>
              {applying ? 'Processing…' : 'Uploading…'}
            </Text>
          </Box>
        )}

        {/* Scrollable body */}
        <Box
          sx={{
            flex: '1 1 0',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 24,
            paddingRight: 2
          }}
        >
          <Stack spacing="xl">
            {previewUrl && (
              <Box
                ref={containerRef as any}
                sx={{
                  position: 'relative',
                  lineHeight: 0,
                  borderRadius: 12,
                  overflow: 'hidden',
                  border: '2px solid var(--gs-border)',
                  width: '100%',
                  minHeight: '50dvh',
                  height: '58dvh',
                  maxHeight: 'min(78dvh, 900px)',
                  background: '#000'
                }}
              >
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    background: '#000'
                  }}
                />
                {/* Canvas is positioned dynamically in redrawCanvas to match the
                    image's actual rendered area (objectFit:contain may add bars). */}
                <canvas
                  ref={canvasRef}
                  style={{
                    position: 'absolute',
                    pointerEvents: 'none'
                  }}
                />
              </Box>
            )}

            {ocrStatus === 'idle' && regions.length === 0 && (
              <Alert icon={<AlertIcon size={22} />} color="yellow" variant="light" sx={{ fontSize: 17, lineHeight: 1.55 }}>
                Photos of firearms may contain serial numbers. You can auto-detect and blur them before uploading.
              </Alert>
            )}

            {ocrStatus === 'error' && (
              <Alert color="red" variant="light" sx={{ fontSize: 17, lineHeight: 1.55 }}>
                Detection failed. You can still upload the original or try again.
              </Alert>
            )}

            {isScanning && (
              <Group spacing="md">
                <Loader size="md" />
                <Text size="lg" color="dimmed">
                  {STATUS_LABELS[ocrStatus]}
                </Text>
              </Group>
            )}

            {ocrStatus === 'done' && regions.length === 0 && (
              <Text size="lg" color="dimmed" sx={{ lineHeight: 1.55 }}>
                No serial-number-like text detected. Your photo looks safe to upload.
              </Text>
            )}

            {regions.length > 0 && (
              <Stack spacing="lg">
                <Text size="lg" weight={700} sx={{ lineHeight: 1.45 }}>
                  Detected text — turn off anything you don&apos;t want blurred:
                </Text>
                <Stack spacing="lg">
                  {regions.map((r) => (
                    <Box
                      key={r.id}
                      sx={{
                        padding: '18px 18px',
                        borderRadius: 12,
                        border: '2px solid var(--gs-border)',
                        background: 'var(--gs-surface)'
                      }}
                    >
                      <Group position="apart" align="center" spacing="lg" noWrap>
                        <Text
                          component="span"
                          sx={{
                            fontFamily: 'monospace',
                            letterSpacing: 1,
                            fontSize: 16,
                            fontWeight: 600,
                            color: r.blur ? 'var(--gs-text)' : 'var(--gs-muted)',
                            flex: 1,
                            minWidth: 0,
                            wordBreak: 'break-all',
                            lineHeight: 1.35
                          }}
                        >
                          {r.text}
                        </Text>
                        <Switch
                          checked={r.blur}
                          onChange={() => toggleRegion(r.id)}
                          label={r.blur ? 'Blur' : 'Skip'}
                          size="lg"
                          styles={{ label: { fontSize: 16, fontWeight: 500 } }}
                          sx={{ flexShrink: 0 }}
                        />
                      </Group>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            )}

            {/* Blur apply error — shown when applyBlurs throws (image decode failure, canvas OOM, etc.) */}
            {applyError && (
              <Alert color="red" variant="filled" icon={<AlertIcon size={16} />}>
                <Text size="sm" weight={600}>Blur failed — you can still upload the original</Text>
                <Text size="xs" mt={2} sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {applyError.split('\n')[0]}
                </Text>
              </Alert>
            )}

            {/* Debug panel — file metadata + full error on mobile where devtools aren't available */}
            {debugInfo && (
              <Box sx={{ background: '#111', borderRadius: 6, padding: '8px 10px', fontFamily: 'monospace', fontSize: 11, lineHeight: 1.5, wordBreak: 'break-all', whiteSpace: 'pre-wrap', border: '1px solid #333' }}>
                <Text size="xs" color="dimmed" mb={2} weight={700}>DEBUG INFO</Text>
                <Text size="xs" color="gray.4">Name: {debugInfo.file.name}</Text>
                <Text size="xs" color="gray.4">Size: {debugInfo.file.size}{debugInfo.compressedSize ? ` → ${debugInfo.compressedSize} (compressed)` : ''}</Text>
                <Text size="xs" color={debugInfo.file.type && ['image/jpeg', 'image/png', 'image/webp'].includes(debugInfo.file.type) ? 'green.4' : 'yellow.4'}>
                  MIME: {debugInfo.file.type}
                </Text>
                <Text size="xs" color={debugInfo.canvasSupport ? 'green.4' : 'red.4'}>
                  Canvas 2D: {debugInfo.canvasSupport ? 'OK' : 'NOT SUPPORTED'}
                </Text>
                {debugInfo.error && (
                  <Text size="xs" color="red.4" mt={4}>ERROR: {debugInfo.error}</Text>
                )}
              </Box>
            )}

            {/* Auto-detect button lives in the scroll body so it stays near the relevant alerts */}
            {(ocrStatus === 'idle' || ocrStatus === 'error' || ocrStatus === 'done') && (
              <Button
                variant="light"
                size="lg"
                loading={isScanning}
                disabled={uploading || applying}
                onClick={() => void runDetection()}
                sx={{ touchAction: 'manipulation', minHeight: 54, fontSize: 16 }}
              >
                {ocrStatus === 'done' || ocrStatus === 'error' ? 'Re-scan' : 'Auto-detect serial numbers'}
              </Button>
            )}
          </Stack>
        </Box>

        {/* Fixed footer: outside the scroll area; primary actions stay reachable */}
        <Stack
          spacing="lg"
          sx={{
            flexShrink: 0,
            paddingTop: 20,
            paddingBottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
            paddingLeft: 4,
            paddingRight: 4,
            borderTop: '2px solid var(--gs-border)',
            background: 'var(--gs-bg)'
          }}
        >
          <Button
            type="button"
            variant="default"
            size="lg"
            disabled={uploading || applying}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void handleSkipUpload()
            }}
            sx={actionBtnSx}
          >
            Upload without blurring
          </Button>
          <Button
            type="button"
            size="lg"
            loading={applying}
            disabled={toBlurCount === 0 || uploading || applying}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void handleConfirm()
            }}
            sx={actionBtnSx}
          >
            Blur {toBlurCount} region{toBlurCount !== 1 ? 's' : ''} & upload
          </Button>
        </Stack>
      </Box>
    </Modal>
  )
}
